import {
  CHROME_STORE_EXTENSION_ADDRESS,
  CHROME_STORE_EXTENSION_ID,
  CHROME_STORE_EXTENSION_RTP_ADDRESS,
  CHROME_STORE_EXTENSION_RTP_ID,
  DOMAIN_ID,
} from "./constants";

const EXTENSIONS = {
  SALESSTREAM: "salesstream",
  REACHTHEPEOPLE: "reachthepeople",
};

const ENVs = {
  salesstream: {
    public: {
      main: {
        local: "http://localhost:3000",
        dev: "https://dev-web.salesstream.ai",
        qa: "https://qa-web.salesstream.ai",
        prod: "https://web.salesstream.ai",
      },
      admin: {
        local: "http://localhost:3002",
        dev: "https://admin.dev.salesstream.ai",
        qa: "https://admin.qa.salesstream.ai",
        prod: "https://admin.salesstream.ai",
      },
    },
    api: {
      local: "http://localhost:7071/api",
      dev: "https://dev-app.salesstream.ai/api",
      qa: "https://qa-app.salesstream.ai/api",
      prod: "https://app.salesstream.ai/api",
    },
  },
  reachthepeople: {
    public: {
      main: {
        local: "http://localhost:3003",
        dev: "https://dev-web.reachthepeople.ai",
        qa: "https://qa-web.reachthepeople.ai",
        prod: "https://web.reachthepeople.ai",
      },
      admin: {
        local: "http://localhost:3004",
        dev: "https://admin.dev.reachthepeople.ai",
        qa: "https://admin.qa.reachthepeople.ai",
        prod: "https://admin.reachthepeople.ai",
      },
      revintel: {
        local: "http://localhost:3005",
        dev: "https://revintel.dev.reachthepeople.ai",
        qa: "https://revintel.qa.reachthepeople.ai",
        prod: "https://revintel.reachthepeople.ai",
      },
    },
    api: {
      local: "http://localhost:7071/api",
      dev: "https://dev-app.reachthepeople.ai/api",
      qa: "https://qa-app.reachthepeople.ai/api",
      prod: "https://app.reachthepeople.ai/api",
    },
  },
};

export const getCurrentEnv = () => {
  const origin = window.location.origin;

  if (origin.includes("localhost")) {
    return "local";
  }

  if (
    origin.includes("dev-web") ||
    origin.includes("admin.dev") ||
    origin.includes("revintel.dev")
  ) {
    return "dev";
  }

  if (
    origin.includes("qa-web") ||
    origin.includes("admin.qa") ||
    origin.includes("revintel.qa")
  ) {
    return "qa";
  }

  return "prod";
};

export const getCurrentApp = () => {
  const origin = window.location.origin;

  const salesstreamDomains = [
    ...Object.values(ENVs.salesstream.public.main),
    ...Object.values(ENVs.salesstream.public.admin),
  ];

  const reachthepeopleDomains = [
    ...Object.values(ENVs.reachthepeople.public.main),
    ...Object.values(ENVs.reachthepeople.public.admin),
    ...Object.values(ENVs.reachthepeople.public.revintel),
  ];

  if (salesstreamDomains.includes(origin)) {
    return EXTENSIONS.SALESSTREAM;
  }

  if (reachthepeopleDomains.includes(origin)) {
    return EXTENSIONS.REACHTHEPEOPLE;
  }

  return EXTENSIONS.SALESSTREAM;
};

export const getApiUrl = () => {
  const currentApp = getCurrentApp();
  const currentEnv = getCurrentEnv();

  const apiUrls = {
    [EXTENSIONS.SALESSTREAM]: {
      local: ENVs.salesstream.api.local,
      dev: ENVs.salesstream.api.dev,
      qa: ENVs.salesstream.api.qa,
      prod: ENVs.salesstream.api.prod,
    },
    [EXTENSIONS.REACHTHEPEOPLE]: {
      local: ENVs.reachthepeople.api.local,
      dev: ENVs.reachthepeople.api.dev,
      qa: ENVs.reachthepeople.api.qa,
      prod: ENVs.reachthepeople.api.prod,
    },
  };

  return apiUrls[currentApp][currentEnv];
};

export const getPublicUrl = () => {
  const currentApp = getCurrentApp();
  const currentEnv = getCurrentEnv();

  const publicUrls = {
    [EXTENSIONS.SALESSTREAM]: {
      local: ENVs.salesstream.public.main.local,
      dev: ENVs.salesstream.public.main.dev,
      qa: ENVs.salesstream.public.main.qa,
      prod: ENVs.salesstream.public.main.prod,
    },
    [EXTENSIONS.REACHTHEPEOPLE]: {
      local: ENVs.reachthepeople.public.main.local,
      dev: ENVs.reachthepeople.public.main.dev,
      qa: ENVs.reachthepeople.public.main.qa,
      prod: ENVs.reachthepeople.public.main.prod,
    },
  };

  return publicUrls[currentApp][currentEnv];
};

export const isAdminSite = () => {
  const origin = window.location.origin;
  const currentApp = getCurrentApp();
  const adminDomains = Object.values(ENVs[currentApp].public.admin);

  return adminDomains.includes(origin);
};

export const isRevIntelSite = () => {
  const origin = window.location.origin;
  const currentApp = getCurrentApp();
  const revintelDomains = Object.values(ENVs[currentApp].public.revintel || {});

  return revintelDomains.includes(origin);
};

// The Revenue Intelligence site for this app and environment ("" where there is none).
export const getRevIntelUrl = () =>
  ENVs[getCurrentApp()]?.public.revintel?.[getCurrentEnv()] || "";

export const getAppConfig = () => {
  const currentApp = getCurrentApp();

  return APP_CONFIGS[currentApp];
};

// The variant is resolved at runtime by domain, but index.html ships a static SalesStream title +
// favicon. Apply the active variant's branding to the document so the RTP tab shows the right ones.
export const applyAppBranding = () => {
  const config = getAppConfig();
  if (!config) return;

  if (config.title) {
    document.title = config.title;
  }

  if (config.favicon) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = config.favicon;
  }
};

export const isSalesstream = () => {
  return getCurrentApp() === EXTENSIONS.SALESSTREAM;
};

export const isReachthepeople = () => {
  return getCurrentApp() === EXTENSIONS.REACHTHEPEOPLE;
};

export const checkCurrentApp = () => {
  const curApp = getCurrentApp();
  return {
    isSalesstreamApp: curApp === EXTENSIONS.SALESSTREAM,
    isReachthepeopleApp: curApp === EXTENSIONS.REACHTHEPEOPLE,
  };
};

export const getDomainId = () => {
  if (isReachthepeople()) {
    return DOMAIN_ID.REACHTHEPEOPLE;
  }

  return DOMAIN_ID.SALESSTREAM;
};

export const getChromeStoreExtensionId = () => {
  if (isReachthepeople()) {
    return CHROME_STORE_EXTENSION_RTP_ID;
  }

  return CHROME_STORE_EXTENSION_ID;
};

export const getChromeStoreExtensionAddress = () => {
  if (isReachthepeople()) {
    return CHROME_STORE_EXTENSION_RTP_ADDRESS;
  }

  return CHROME_STORE_EXTENSION_ADDRESS;
};

export const APP_CONFIGS = {
  [EXTENSIONS.SALESSTREAM]: {
    name: "SalesStream.ai",
    shortName: "SalesStream",
    title: "Reach from SalesStream.ai",
    favicon: "/favicon.ico",
    theme: {
      isSalesstream: true,
      logoPath: "/images/logos/full-logo.svg",
      miniLogoPath: "/images/logos/logo-blue.svg",
      showPasswordIconPath: "/images/show-pwd-icon.svg",
      hidePasswordIconPath: "/images/hide-pwd-icon.svg",
      primaryTextColor: "#000000",
      secondaryTextColor: "#333333",
      tertiaryTextColor: "#00000066",
      accentTextColor: "#0872D3",
      primaryFontWeight: "600",
      secondaryFontWeight: "500",
      primaryBorderRadius: "40px",
      fonts: {
        primary: "'Inter', sans-serif",
        lineHeight: "22px",
      },
      colors: {
        primaryColor: "#0872d3",
        hoverPrimaryColor: "#0561b5",
        completedColor: "#08d37b",
      },
      buttonVariants: {
        primary: {
          backgroundColor: "#0872d3",
          borderRadius: "40px",
          color: "#ffffff",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "22px",
          iconPath: "/images/arrow-down-blue.svg",
          hover: {
            backgroundColor: "#3B95E7",
          },
          active: {
            backgroundColor: "#0565BC",
          },
          disabled: {
            backgroundColor: "#cadef0",
            iconPath: "/images/arrow-down-light-blue.svg",
          },
        },
        secondary: {
          backgroundColor: "#0872d3",
          borderColor: "#cadef0",
          borderRadius: "40px",
          color: "#ffffff",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "22px",
          hover: {
            backgroundColor: "#3B95E7",
          },
          active: {
            backgroundColor: "#0565BC",
          },
          disabled: {
            backgroundColor: "#cadef0",
          },
        },
        tertiary: {
          backgroundColor: "transparent",
          borderColor: "#cadef0",
          borderRadius: "40px",
          color: "#0872d3",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "22px",
          hover: {
            backgroundColor: "transparent",
            borderColor: "#3B95E7",
            color: "#0872D3",
          },
          active: {
            backgroundColor: "transparent",
            borderColor: "#0565BC",
            color: "#0565BC",
          },
          disabled: {
            backgroundColor: "transparent",
            borderColor: "#CADEF0",
            color: "#CADEF0",
          },
        },
        oauth: {
          color: "#0872D3",
          border: "1.5px solid #CADEF0",
          backgroundColor: "transparent",
          hoverBackgroundColor: "#f8f8f8",
          hoverBorderColor: "rgba(5, 101, 188, 0.4)",
          activeBackgroundColor: "#f8f8f8",
          activeBorderColor: "rgba(5, 101, 188, 0.8)",
        },
        close: {
          borderRadius: "100px",
          border: "1px solid #F0F0F0",
          fill: "#000000",
        },
        signUpBtn: {
          border: "1px solid #cadef0",
          borderRadius: "40px",
          color: "#0872d3",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "22px",
          hoverBackgroundColor: "#f8f8f8",
          hoverBorderColor: "rgba(5, 101, 188, 0.4)",
        },
      },
      separatorVariants: {
        auth: {
          color: " rgba(0, 0, 0, 0.4)",
          fontWeight: "500",
          backgroundColor: "#F0F0F0",
        },
      },
      filterVariants: {
        tertiary: {
          padding: "4px 12px 4px 16px",
          gap: "12px",
          backgroundColor: "#FFFFFF",
          borderColor: "#f0f0f0",
          borderRadius: "40px",
          color: "#030C23",
          fontWeight: "500",
          fontSize: "14px",
          lineHeight: "22px",
          hover: {
            backgroundColor: "#FFFFFF",
            borderColor: "#93949d",
            color: "#030C23",
          },
          active: {
            backgroundColor: "#FFFFFF",
            borderColor: "#0872D3",
            color: "#030C23",
          },
          disabled: {
            backgroundColor: "#1D1D1D0A",
            borderColor: "#1D1D1D01",
            color: "#1D1D1D80",
          },
          dropdown: {
            iconPath: "/images/arrow-down-blue.svg",
            width: "16px",
          },
          calendar: {
            iconPath: "/images/calendar/salesstream/icon-dark.svg",
            width: "20px",
            active: {
              iconPath: "/images/calendar/salesstream/icon-blue.svg",
            },
          },
        },
      },
      inputLabel: {
        fontWeight: "600",
        fontSize: "14px",
        color: "#0872D3",
        lineHeight: "20px",
      },
      input: {
        border: "1px solid #c6c8d5",
        borderColor: "#c6c8d5",
        borderRadius: "40px",
        padding: "10px 24px",
        fontWeight: "500",
        fontSize: "16px",
        color: "#000000",
        lineHeight: "22px",
        placeholderColor: "rgba(0, 0, 0, 0.4)",
        hoverBorderColor: "#93949d",
        focusBorderColor: "#0872d3",
      },
      titleVariants: {
        auth: {
          color: "#000000",
          fontWeight: "600",
          fontSize: "20px",
          lineHeight: "24px",
        },
      },
      chat: {
        colors: {
          primary: "#0872d3",
        },
        highlightLineImgPath: "/images/chat/highlight-line-blue.svg",
      },
      table: {
        borderRadius: "12px",
        borderColor: "#f0f0f0",
        boxShadowColor: "#0000000a",
        head: {
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "22px",
          color: "#00000066",
        },
        body: {
          fontWeight: "500",
          fontSize: "14px",
          lineHeight: "22px",
          color: "#00000066",
          highlightColor: "#0872d3",
          highlightColorHovered: "#0561b5",
          highlightDarkColor: "#000000",
        },
      },
      toggle: {
        width: "39px",
        height: "22px",
        circleWidth: "16px",
        circleHeight: "16px",
        circleTop: "3px",
        circleLeft: "3px",
        circleLeftChecked: "20px",
        activeBackgroundColor: "#0872d3",
        inactiveBackgroundColor: "#00000066",
      },
    },
  },
  [EXTENSIONS.REACHTHEPEOPLE]: {
    name: "Reach The People",
    shortName: "Reach",
    title: "Reach from ReachThePeople.ai",
    favicon: "/images/logos/minimal-logo-reachthepeople.png",
    theme: {
      isReachthepeople: true,
      logoPath: "/images/logos/reach-the-people-logo.svg",
      miniLogoPath: "/images/logos/minimal-logo-reachthepeople.png",
      showPasswordIconPath: "/images/show-pwd-icon-reachthepeople.svg",
      hidePasswordIconPath: "/images/hide-pwd-icon.svg",
      primaryTextColor: "#1D1D1DE5",
      secondaryTextColor: "#1D1D1D80",
      tertiaryTextColor: "#1D1D1D80",
      accentTextColor: "#FF6030",
      primaryFontWeight: "500",
      secondaryFontWeight: "400",
      primaryBorderRadius: "12px",
      fonts: {
        primary: "'Switzer', sans-serif",
        lineHeight: "24px",
      },
      colors: {
        primaryColor: "#FF6030",
        hoverPrimaryColor: "#F84C1D",
        completedColor: "#00C17A",
      },
      buttonVariants: {
        primary: {
          backgroundColor: "#FF6030",
          borderRadius: "12px",
          color: "#ffffff",
          fontWeight: "600",
          fontSize: "16px",
          lineHeight: "24px",
          iconPath: "/images/arrow-right-white-reachthepeople.svg",
          hover: {
            backgroundColor: "#F84C1D",
          },
          active: {
            backgroundColor: "#FC714A",
          },
          disabled: {
            backgroundColor: "#FFAB95",
          },
        },
        secondary: {
          backgroundColor: "#1D1D1D",
          borderRadius: "12px",
          color: " #FFFFFF",
          fontWeight: "600",
          fontSize: "16px",
          lineHeight: "24px",
          hover: {
            backgroundColor: "#1D1D1DCC",
          },
          active: {
            backgroundColor: "#1D1D1DB2",
          },
          disabled: {
            backgroundColor: "#1D1D1D80",
          },
        },
        tertiary: {
          backgroundColor: "#FFFFFF",
          borderColor: "#1D1D1D1A",
          borderRadius: "12px",
          color: "#1D1D1D",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "24px",
          hover: {
            backgroundColor: "#1D1D1D0A",
            borderColor: "#1D1D1D1A",
            color: "#1D1D1D",
          },
          active: {
            backgroundColor: "#1D1D1D1A",
            borderColor: "#1D1D1D33",
            color: "#1D1D1DB2",
          },
          disabled: {
            backgroundColor: "#1D1D1D0A",
            borderColor: "#1D1D1D01",
            color: "#1D1D1D80",
          },
        },

        oauth: {
          color: "#1D1D1D",
          border: "1px solid #1D1D1D1A",
          backgroundColor: "transparent",
          hoverBackgroundColor: "#f8f8f8",
          hoverBorderColor: "#1D1D1D1A",
          activeBackgroundColor: "#f8f8f8",
          activeBorderColor: "#1D1D1D1A",
        },
        close: {
          borderRadius: "12px",
          border: "1px solid rgba(29, 29, 29, 0.1)",
          fill: "#1D1D1DE5",
        },
        signUpBtn: {
          border: "1px solid #1D1D1D1A",
          borderRadius: "12px",
          color: "#1D1D1D",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "22px",
          hoverBackgroundColor: "#1D1D1D1A",
          hoverBorderColor: "#1D1D1D1A",
        },
      },
      separatorVariants: {
        auth: {
          color: "#1D1D1D80",
          fontWeight: "400",
          backgroundColor: "#1D1D1D1A",
        },
      },
      filterVariants: {
        tertiary: {
          padding: "0 20px",
          gap: "8px",
          backgroundColor: "#FFFFFF",
          borderColor: "#1D1D1D1A",
          borderRadius: "12px",
          color: "#1D1D1D",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "24px",
          hover: {
            backgroundColor: "#1D1D1D0A",
            borderColor: "#1D1D1D1A",
            color: "#1D1D1D",
          },
          active: {
            backgroundColor: "#1D1D1D1A",
            borderColor: "#1D1D1D33",
            color: "#1D1D1DB2",
          },
          disabled: {
            backgroundColor: "#1D1D1D0A",
            borderColor: "#1D1D1D01",
            color: "#1D1D1D80",
          },
          dropdown: {
            iconPath: "/images/arrow-down-dark.svg",
            width: "20px",
          },
          calendar: {
            iconPath: "/images/calendar/reachpeople/icon-dark.svg",
            width: "20px",
            active: {
              iconPath: "/images/calendar/reachpeople/icon-dark.svg",
            },
          },
        },
      },
      inputLabel: {
        fontWeight: "400",
        fontSize: "14px",
        color: "rgba(29, 29, 29, 0.9)",
        lineHeight: "21px",
      },
      input: {
        border: "1px solid rgba(29, 29, 29, 0.1)",
        borderColor: "rgba(29, 29, 29, 0.1)",
        borderRadius: "12px",
        padding: "10px 20px",
        fontWeight: "400",
        fontSize: "16px",
        color: "rgba(29, 29, 29, 0.9)",
        lineHeight: "24px",
        placeholderColor: "#1D1D1D80",
        hoverBorderColor: "#1D1D1D33",
        focusBorderColor: "#1D1D1D",
      },
      titleVariants: {
        auth: {
          color: "rgba(29, 29, 29, 0.9)",
          fontWeight: "500",
          fontSize: "24px",
          lineHeight: "31px",
        },
      },
      chat: {
        colors: {
          primary: "#FF6030",
        },
      },
      table: {
        // borderRadius: "16px",
        borderRadius: "15px",
        borderColor: "#1D1D1D1A",
        boxShadowColor: "#E8E8E8",
        head: {
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "24px",
          color: "#1D1D1D80",
        },
        body: {
          fontWeight: "400",
          fontSize: "14px",
          lineHeight: "21px",
          color: "#1D1D1D80",
          highlightColor: "#FF6030",
          highlightColorHovered: "#F84C1D",
          highlightDarkColor: "#1D1D1DE5",
        },
      },
      toggle: {
        width: "44px",
        height: "24px",
        circleWidth: "20px",
        circleHeight: "20px",
        circleTop: "2px",
        circleLeft: "3px",
        circleLeftChecked: "22px",
        activeBackgroundColor: "#FF6030",
        inactiveBackgroundColor: "#E8E8E8",
      },
    },
  },
};
